import React, { useState, useEffect } from 'react';
import { useCRM } from '../../contexts/CRMContext';
import { useBranding } from '../../contexts/BrandingContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import FeatureGate from './FeatureGate';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import crmApi, { clientAPI } from '../../utils/crmApi';
import { 
  SparklesIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  LightBulbIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const AICopilot = () => {
  const { user, currentAgency } = useCRM();
  const { branding } = useBranding();
  const { hasFeature } = useSubscription();
  
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [activeAction, setActiveAction] = useState(null);
  const [result, setResult] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');

  useEffect(() => {
    if (currentAgency?._id && hasFeature('aiCopilot')) {
      loadHistory();
      loadAnalytics();
      loadClients();
    }
  }, [currentAgency?._id, hasFeature]);

  const loadClients = async () => {
    if (!currentAgency?._id) return;
    try {
      const response = await clientAPI.getAll({ limit: 50 });
      const data = response.data;
        if (data?.success) {
          const list = Array.isArray(data.data) ? data.data : (data.clients || []);
          setClients(list);
          if (!selectedClientId && list.length > 0) {
            setSelectedClientId(list[0]._id || list[0].id);
        }
      }
    } catch (e) {
      // ignore
    }
  };

  const loadHistory = async () => {
    if (!currentAgency?._id) return;
    
    try {
      const response = await crmApi.get('/ai-copilot/history', { params: { limit: 10 } });
      const data = response.data;
        if (data.success) {
          setHistory(data.data.aiActions || []);
      }
    } catch (error) {
      console.error('Failed to load AI history:', error);
    }
  };

  const loadAnalytics = async () => {
    if (!currentAgency?._id) return;
    
    try {
      const response = await crmApi.get('/ai-copilot/analytics', { params: { period: '30d' } });
      const data = response.data;
        if (data.success) {
          setAnalytics(data.data);
      }
    } catch (error) {
      console.error('Failed to load AI analytics:', error);
    }
  };

  const handleEmailGeneration = async () => {
    if (!currentAgency?._id) return;
    if (!selectedClientId) {
      toast.error('Please select a client first');
      return;
    }
    
    setLoading(true);
    setActiveAction('email_generation');
    
    try {
      const response = await crmApi.post('/ai-copilot/email-generation', {
          clientId: selectedClientId,
          context: 'Generate a professional follow-up email',
          tone: 'professional',
          language: 'en'
      });
      
      const data = response.data;
        if (data.success) {
          setResult(data.data);
          toast.success('Email generated successfully!');
          loadHistory();
      } else {
        throw new Error(data.message || 'Failed to generate email');
      }
    } catch (error) {
      console.error('Email generation error:', error);
      toast.error('Failed to generate email');
    } finally {
      setLoading(false);
    }
  };

  const handleCaseSummarization = async () => {
    if (!currentAgency?._id) return;
    if (!selectedClientId) {
      toast.error('Please select a client first');
      return;
    }
    
    setLoading(true);
    setActiveAction('case_summarization');
    
    try {
      const response = await crmApi.post('/ai-copilot/case-summarization', {
          clientId: selectedClientId,
          includeTasks: true,
          includeCommunications: true,
          includeDocuments: true
      });
      
      const data = response.data;
        if (data.success) {
          setResult(data.data);
          toast.success('Case summary generated!');
          loadHistory();
      } else {
        throw new Error(data.message || 'Failed to generate summary');
      }
    } catch (error) {
      console.error('Case summarization error:', error);
      toast.error('Failed to generate case summary');
    } finally {
      setLoading(false);
    }
  };

  const handleNextBestAction = async () => {
    if (!currentAgency?._id) return;
    if (!selectedClientId) {
      toast.error('Please select a client first');
      return;
    }
    
    setLoading(true);
    setActiveAction('next_best_action');
    
    try {
      const response = await crmApi.post('/ai-copilot/next-best-action', {
          clientId: selectedClientId,
          context: 'Get recommendations for client engagement'
      });
      
      const data = response.data;
        if (data.success) {
          setResult(data.data);
          toast.success('Recommendations generated!');
          loadHistory();
      } else {
        throw new Error(data.message || 'Failed to get recommendations');
      }
    } catch (error) {
      console.error('Next best action error:', error);
      toast.error('Failed to get recommendations');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'email_generation':
        return EnvelopeIcon;
      case 'case_summarization':
        return DocumentTextIcon;
      case 'next_best_action':
        return LightBulbIcon;
      default:
        return SparklesIcon;
    }
  };

  const getActionLabel = (actionType) => {
    switch (actionType) {
      case 'email_generation':
        return 'Email Generation';
      case 'case_summarization':
        return 'Case Summary';
      case 'next_best_action':
        return 'Next Best Action';
      default:
        return 'AI Action';
    }
  };

  return (
    <FeatureGate feature="aiCopilot" requiredTier="Enterprise">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center">
              <SparklesIcon className="h-7 w-7 mr-2" style={{ color: branding.primaryColor }} />
              AI Copilot
            </h1>
            <p className="text-muted-foreground mt-1">
              AI-powered assistance for smarter workflows and client engagement
            </p>
          </div>
        </div>

        {/* Client Selector */}
        <Card className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-1">Select Client</label>
              <select
                value={selectedClientId}
                onChange={(e) => setSelectedClientId(e.target.value)}
                className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
              >
                <option value="">-- Choose a client --</option>
                {clients.map((c) => (
                  <option key={c._id || c.id} value={c._id || c.id}>{c.name || c.email || (c._id || c.id)}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={loadClients} disabled={loading}>Refresh</Button>
              <Button variant="outline" onClick={() => selectedClientId && window.open(`/clients/${selectedClientId}`, '_blank') } disabled={!selectedClientId}>Open Client</Button>
            </div>
          </div>
        </Card>

        {/* Analytics Overview */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="p-4">
              <div className="text-sm text-muted-foreground">Total Actions</div>
              <div className="text-2xl font-bold text-foreground mt-1">{analytics.totalActions || 0}</div>
            </Card>
            <Card className="p-4">
              <div className="text-sm text-muted-foreground">Success Rate</div>
              <div className="text-2xl font-bold text-green-600 mt-1">
                {analytics.analytics?.[0]?.successRate 
                  ? `${Math.round(analytics.analytics[0].successRate * 100)}%`
                  : 'N/A'}
              </div>
            </Card>
            <Card className="p-4">
              <div className="text-sm text-muted-foreground">Avg Confidence</div>
              <div className="text-2xl font-bold text-blue-600 mt-1">
                {analytics.analytics?.[0]?.avgConfidence 
                  ? `${Math.round(analytics.analytics[0].avgConfidence * 100)}%`
                  : 'N/A'}
              </div>
            </Card>
            <Card className="p-4">
              <div className="text-sm text-muted-foreground">Period</div>
              <div className="text-2xl font-bold text-foreground mt-1">Last 30 days</div>
            </Card>
          </div>
        )}

        {/* Quick Actions */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={handleEmailGeneration}
              disabled={loading}
              className="p-4 border border-border rounded-lg hover:bg-muted transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center mb-2">
                <EnvelopeIcon className="h-5 w-5 mr-2" style={{ color: branding.primaryColor }} />
                <span className="font-medium text-foreground">Generate Email</span>
              </div>
              <p className="text-sm text-muted-foreground">
                AI-powered email generation with personalized content
              </p>
            </button>

            <button
              onClick={handleCaseSummarization}
              disabled={loading}
              className="p-4 border border-border rounded-lg hover:bg-muted transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center mb-2">
                <DocumentTextIcon className="h-5 w-5 mr-2" style={{ color: branding.primaryColor }} />
                <span className="font-medium text-foreground">Summarize Case</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Get comprehensive case overview and status summary
              </p>
            </button>

            <button
              onClick={handleNextBestAction}
              disabled={loading}
              className="p-4 border border-border rounded-lg hover:bg-muted transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center mb-2">
                <LightBulbIcon className="h-5 w-5 mr-2" style={{ color: branding.primaryColor }} />
                <span className="font-medium text-foreground">Next Best Action</span>
              </div>
              <p className="text-sm text-muted-foreground">
                AI-powered recommendations for client engagement
              </p>
            </button>
          </div>
        </Card>

        {/* Loading State */}
        {loading && (
          <Card className="p-6">
            <div className="flex items-center justify-center">
              <ArrowPathIcon className="h-6 w-6 animate-spin mr-2" style={{ color: branding.primaryColor }} />
              <span className="text-foreground">AI is working...</span>
            </div>
          </Card>
        )}

        {/* Result Display */}
        {result && !loading && (
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Result</h3>
              <button
                onClick={() => setResult(null)}
                className="text-muted-foreground hover:text-foreground"
              >
                <XCircleIcon className="h-5 w-5" />
              </button>
            </div>
            {result.generatedEmail && (
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-foreground mb-2">Subject:</h4>
                  <p className="text-muted-foreground">{result.generatedEmail.subject}</p>
                </div>
                <div>
                  <h4 className="font-medium text-foreground mb-2">Body:</h4>
                  <p className="text-muted-foreground whitespace-pre-wrap">{result.generatedEmail.body}</p>
                </div>
              </div>
            )}
            {result.summary && (
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-foreground mb-2">Client Overview:</h4>
                  <div className="bg-muted p-3 rounded">
                    <p className="text-sm text-muted-foreground">
                      {result.summary.clientOverview?.name} - {result.summary.clientOverview?.status}
                    </p>
                  </div>
                </div>
                {result.summary.keyHighlights && (
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Key Highlights:</h4>
                    <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                      {result.summary.keyHighlights.map((highlight, index) => (
                        <li key={index}>{highlight}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            {result.recommendations && (
              <div className="space-y-3">
                <h4 className="font-medium text-foreground mb-2">Recommendations:</h4>
                {result.recommendations.map((rec, index) => (
                  <div key={index} className="border border-border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-foreground">{rec.action}</span>
                      <Badge variant={rec.priority === 'high' ? 'destructive' : 'secondary'}>
                        {rec.priority}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{rec.reason}</p>
                    <div className="flex items-center space-x-4 mt-2 text-xs text-muted-foreground">
                      <span>⏱ {rec.estimatedTime}</span>
                      <span>💡 {rec.impact}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* History */}
        {history.length > 0 && (
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Recent Activity</h2>
            <div className="space-y-3">
              {history.map((action) => {
                const Icon = getActionIcon(action.actionType);
                return (
                  <div key={action._id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Icon className="h-5 w-5" style={{ color: branding.primaryColor }} />
                      <div>
                        <div className="font-medium text-foreground">{getActionLabel(action.actionType)}</div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(action.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {action.status === 'completed' && (
                        <CheckCircleIcon className="h-5 w-5 text-green-600" />
                      )}
                      {action.status === 'pending' && (
                        <ClockIcon className="h-5 w-5 text-yellow-600" />
                      )}
                      {action.status === 'failed' && (
                        <XCircleIcon className="h-5 w-5 text-red-600" />
                      )}
                      <Badge variant={action.confidence >= 0.8 ? 'default' : 'secondary'}>
                        {Math.round(action.confidence * 100)}% confidence
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Empty State */}
        {!loading && history.length === 0 && !result && (
          <Card className="p-12 text-center">
            <SparklesIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Get Started with AI Copilot</h3>
            <p className="text-muted-foreground mb-6">
              Use AI to generate emails, summarize cases, and get recommendations for better client engagement.
            </p>
            <div className="flex items-center justify-center space-x-4">
              <Button onClick={handleEmailGeneration} className="text-white" style={{ backgroundColor: branding.primaryColor }}>
                <EnvelopeIcon className="h-4 w-4 mr-2" />
                Generate Email
              </Button>
              <Button onClick={handleCaseSummarization} variant="outline">
                <DocumentTextIcon className="h-4 w-4 mr-2" />
                Summarize Case
              </Button>
              <Button onClick={handleNextBestAction} variant="outline">
                <LightBulbIcon className="h-4 w-4 mr-2" />
                Get Recommendations
              </Button>
            </div>
          </Card>
        )}
      </div>
    </FeatureGate>
  );
};

export default AICopilot;
